import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, BellOff, Check, Clock3, X } from 'lucide-react';
import { Button } from '../ui/button';

const MUTE_DURATIONS = [
  { label: '1 orë', seconds: 3600 },
  { label: '8 orë', seconds: 28800 },
  { label: '24 orë', seconds: 86400 },
  { label: '1 javë', seconds: 604800 },
  { label: 'Derisa ta ndez përsëri', seconds: null },
];

const MuteConversationDialog = ({ open, conversationName, onClose, onMute }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleMute = async (seconds) => {
    setIsSaving(true);
    setError('');
    try {
      const succeeded = await onMute(seconds);
      if (succeeded === false) {
        setError('Biseda nuk u hesht. Provo përsëri.');
        return;
      }
      onClose();
    } catch (muteError) {
      console.error('Unable to mute chat notifications:', muteError);
      setError('Biseda nuk u hesht. Provo përsëri.');
    } finally {
      setIsSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSaving) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="mute-conversation-title"
        aria-describedby="mute-conversation-description"
        className="w-full max-w-md overflow-hidden rounded-2xl border-0 bg-white text-zinc-950 shadow-2xl"
      >
        <header className="flex items-start gap-3 border-b border-zinc-100 p-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
            <BellOff size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="mute-conversation-title" className="text-sm font-bold">Njoftimet pa zë</h2>
            <p id="mute-conversation-description" className="mt-1 text-xs leading-relaxed text-zinc-600">
              Zgjidhni sa kohë t’i mbani njoftimet e kësaj bisede pa zë{conversationName ? ` me ${conversationName}` : ''}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="shrink-0 rounded-lg p-1.5 text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-50"
            aria-label="Mbyll dritaren"
            title="Mbyll"
          >
            <X size={18} />
          </button>
        </header>

        <div className="space-y-2 p-5">
          {MUTE_DURATIONS.map(({ label, seconds }) => (
            <button
              key={label}
              type="button"
              disabled={isSaving}
              onClick={() => handleMute(seconds)}
              className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white px-3.5 py-3 text-left transition hover:border-amber-300 hover:bg-amber-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex items-center gap-2 text-xs font-semibold text-zinc-900">
                {seconds === null
                  ? <Bell size={15} className="shrink-0 text-amber-700" />
                  : <Clock3 size={15} className="shrink-0 text-amber-700" />}
                {label}
              </span>
              {seconds === null && <Check size={15} className="text-amber-700" />}
            </button>
          ))}
          {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
        </div>

        <footer className="flex justify-end border-t border-zinc-100 bg-zinc-50 px-5 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-xl text-xs text-zinc-700 hover:bg-zinc-200"
          >
            Anulo
          </Button>
        </footer>
      </section>
    </div>,
    document.body
  );
};

export default MuteConversationDialog;
